import api from './api';

/**
 * Service pour la gestion des utilisateurs
 */
const userService = {
  /**
   * Récupérer tous les utilisateurs
   * @returns {Promise<Array>} - Liste des utilisateurs
   */
  getAllUsers: async () => {
    try {
      const response = await api.get('/admin/users');
      return response.data.users || [];
    } catch (error) {
      console.error('Error getting users:', error);
      throw error;
    }
  },

  /**
   * Récupérer tous les rôles disponibles
   * @param {boolean} includeInactive - Inclure les rôles inactifs
   * @returns {Promise<Array>} - Liste des rôles disponibles
   */
  getAvailableRoles: async (includeInactive = true) => {
    try {
      const response = await api.get('/roles');
      let roles = response.data.roles || [];

      // Filtrer les rôles inactifs si demandé
      if (!includeInactive) {
        roles = roles.filter(role => role.isActive);
      }

      return roles;
    } catch (error) {
      console.error('Error getting available roles:', error);
      throw error;
    }
  },

  /**
   * Récupérer les rôles d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<Array>} - Liste des rôles de l'utilisateur
   */
  getUserRoles: async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}/roles`);
      return response.data.roles || [];
    } catch (error) {
      console.error(`Error getting roles for user ${userId}:`, error);
      throw error;
    }
  },

  /**
   * Récupérer les détails des rôles d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<Array>} - Liste des détails des rôles de l'utilisateur
   */
  getUserRoleDetails: async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      return response.data.user?.roleDetails || [];
    } catch (error) {
      console.error(`Error getting role details for user ${userId}:`, error);
      throw error;
    }
  },

  /**
   * Attribuer un rôle à un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {string} roleId - ID du rôle
   * @returns {Promise<Object>} - Résultat de l'opération
   */
  assignRoleToUser: async (userId, roleId) => {
    try {
      const response = await api.post('/roles/assign', { userId, roleId });
      return response.data;
    } catch (error) {
      console.error(`Error assigning role to user ${userId}:`, error);
      throw error;
    }
  },

  /**
   * Révoquer un rôle d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {string} roleId - ID du rôle
   * @returns {Promise<Object>} - Résultat de l'opération
   */
  revokeRoleFromUser: async (userId, roleId) => {
    try {
      const response = await api.delete(`/roles/revoke/${userId}/${roleId}`);
      return response.data;
    } catch (error) {
      console.error(`Error revoking role from user ${userId}:`, error);
      throw error;
    }
  },

  /**
   * Récupérer un utilisateur par ID
   * @param {string} id - ID de l'utilisateur
   * @returns {Promise<Object>} - Utilisateur récupéré
   */
  getUser: async (id) => {
    try {
      const response = await api.get(`/admin/users/${id}`);
      return response.data.user;
    } catch (error) {
      console.error(`Error getting user ${id}:`, error);
      throw error;
    }
  },

  /**
   * Créer un nouvel utilisateur
   * @param {Object} userData - Données de l'utilisateur
   * @returns {Promise<Object>} - Utilisateur créé
   */
  createUser: async (userData) => {
    try {
      // Convertir les noms de rôles en IDs si nécessaire
      let processedUserData = { ...userData };

      if (userData.roles && Array.isArray(userData.roles)) {
        // Si userData.roleIds n'existe pas déjà, essayer de convertir les noms de rôles en IDs
        if (!userData.roleIds) {
          try {
            const allRoles = await userService.getAvailableRoles();
            const roleIds = userData.roles
                .map(roleName => {
                  const role = allRoles.find(r => r.name === roleName);
                  return role ? role.id : null;
                })
                .filter(id => id !== null);

            processedUserData.roleIds = roleIds;
          } catch (error) {
            console.error('Error converting role names to IDs:', error);
            // Continuer avec les noms de rôles si la conversion échoue
          }
        }
      }

      const response = await api.post('/admin/users', processedUserData);
      return response.data.user;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  /**
   * Mettre à jour un utilisateur
   * @param {string} id - ID de l'utilisateur
   * @param {Object} userData - Nouvelles données de l'utilisateur
   * @returns {Promise<Object>} - Utilisateur mis à jour
   */
  updateUser: async (id, userData) => {
    try {
      // Convertir les noms de rôles en IDs si nécessaire
      let processedUserData = { ...userData };

      if (userData.roles && Array.isArray(userData.roles)) {
        // Si userData.roleIds n'existe pas déjà, essayer de convertir les noms de rôles en IDs
        if (!userData.roleIds) {
          try {
            const allRoles = await userService.getAvailableRoles();
            const roleIds = userData.roles
                .map(roleName => {
                  const role = allRoles.find(r => r.name === roleName);
                  return role ? role.id : null;
                })
                .filter(id => id !== null);

            processedUserData.roleIds = roleIds;
          } catch (error) {
            console.error('Error converting role names to IDs:', error);
            // Continuer avec les noms de rôles si la conversion échoue
          }
        }
      }

      const response = await api.patch(`/admin/users/${id}`, processedUserData);
      return response.data.user;
    } catch (error) {
      console.error(`Error updating user ${id}:`, error);
      throw error;
    }
  },

  /**
   * Supprimer un utilisateur
   * @param {string} id - ID de l'utilisateur
   * @returns {Promise<boolean>} - True si la suppression a réussi
   */
  deleteUser: async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      return true;
    } catch (error) {
      console.error(`Error deleting user ${id}:`, error);
      throw error;
    }
  },

  /**
   * Vérifier un utilisateur
   * @param {string} id - ID de l'utilisateur
   * @param {string} status - Statut de vérification ('verified', 'rejected')
   * @returns {Promise<Object>} - Utilisateur mis à jour
   */
  verifyUser: async (id, status) => {
    try {
      const response = await api.patch(`/admin/users/${id}/verify`, { status });
      return response.data.user;
    } catch (error) {
      console.error(`Error verifying user ${id}:`, error);
      throw error;
    }
  },

  /**
   * Récupérer les utilisateurs par rôle
   * @param {string} roleId - ID du rôle
   * @returns {Promise<Array>} - Liste des utilisateurs ayant ce rôle
   */
  getUsersByRole: async (roleId) => {
    try {
      const response = await api.get(`/roles/${roleId}/users`);
      return response.data.users || [];
    } catch (error) {
      console.error(`Error getting users for role ${roleId}:`, error);
      throw error;
    }
  },

  /**
   * Récupérer les utilisateurs par nom de rôle
   * @param {string} roleName - Nom du rôle
   * @returns {Promise<Array>} - Liste des utilisateurs ayant ce rôle
   */
  getUsersByRoleName: async (roleName) => {
    try {
      // D'abord, récupérer l'ID du rôle à partir de son nom
      const roles = await userService.getAvailableRoles();
      const role = roles.find(r => r.name === roleName);

      if (!role) {
        console.error(`Role ${roleName} not found`);
        return [];
      }

      return await userService.getUsersByRole(role.id);
    } catch (error) {
      console.error(`Error getting users for role name ${roleName}:`, error);
      throw error;
    }
  },

  /**
   * Vérifier si un utilisateur a un rôle spécifique
   * @param {string} userId - ID de l'utilisateur
   * @param {string} roleName - Nom du rôle
   * @returns {Promise<boolean>} - True si l'utilisateur a le rôle
   */
  userHasRole: async (userId, roleName) => {
    try {
      const userRoles = await userService.getUserRoles(userId);
      return userRoles.some(role => role.name === roleName || role.role === roleName);
    } catch (error) {
      console.error(`Error checking if user ${userId} has role ${roleName}:`, error);
      return false;
    }
  },

  // --- NOUVELLE MÉTHODE ---
  /**
   * Recherche des utilisateurs par nom ou email, en excluant potentiellement un rôle.
   * Appelée par RoleManagement.jsx pour trouver des utilisateurs à assigner.
   * @param {string} query - Terme de recherche (nom ou email).
   * @param {string} [excludeRoleId] - ID optionnel du rôle dont les utilisateurs doivent être exclus des résultats.
   * @returns {Promise<Array>} - Liste des utilisateurs trouvés correspondant aux critères.
   */
  searchUsers: async (query, excludeRoleId = null) => {
    console.debug(`userService.searchUsers called with query: "${query}", excludeRoleId: ${excludeRoleId}`);
    if (!query || query.trim().length < 2) {
      // Ne pas lancer de recherche si la requête est trop courte
      return [];
    }
    try {
      // Construire l'URL avec les paramètres de requête
      const params = new URLSearchParams();
      params.append('query', query);
      if (excludeRoleId) {
        params.append('excludeRoleId', excludeRoleId);
      }

      // Définir l'URL de l'API backend pour la recherche
      // Adaptez cette URL à votre structure d'API (ex: /admin/users/search, /users/search, etc.)
      const apiUrl = `/admin/users/search?${params.toString()}`;
      console.debug(`Calling API: GET ${apiUrl}`);

      const response = await api.get(apiUrl);

      // Assurez-vous que le backend retourne un tableau d'utilisateurs,
      // par exemple dans response.data.users ou directement response.data
      const usersFound = response.data.users || response.data || [];
      console.debug(`userService.searchUsers received ${usersFound.length} users.`);
      return usersFound;

    } catch (error) {
      console.error(`Error searching users with query "${query}":`, error.response?.data || error.message);
      // Vous pouvez choisir de relancer l'erreur ou de retourner un tableau vide
      // throw error;
      return []; // Retourner un tableau vide en cas d'erreur pour éviter de planter l'UI
    }
  },
  // --- FIN NOUVELLE MÉTHODE ---

  /**
   * Vérifier si un utilisateur a un rôle actif spécifique
   * @param {string} userId - ID de l'utilisateur
   * @param {string} roleName - Nom du rôle
   * @returns {Promise<boolean>} - True si l'utilisateur a le rôle actif
   */
  userHasActiveRole: async (userId, roleName) => {
    try {
      const userRoleDetails = await userService.getUserRoleDetails(userId);
      return userRoleDetails.some(role =>
          (role.name === roleName || role.role === roleName) && role.isActive
      );
    } catch (error) {
      console.error(`Error checking if user ${userId} has active role ${roleName}:`, error);
      return false;
    }
  }
};

export default userService;